import { useEffect, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/adminApi";

const HEX = /^#[0-9a-fA-F]{6}$/;

const StyleItem = ({ s, onLib }) => {
  const [name, setName] = useState(s.name);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const saveName = async () => {
    if (!name.trim() || name === s.name) return;
    const { data } = await adminApi.put(`/studio/library/${s.id}`, { name: name.trim() });
    onLib(data); toast.success("Style name saved");
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await adminApi.post(`/studio/library/${s.id}/image`, fd); onLib(data); toast.success("Image replaced"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Upload failed")); }
    finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <div className="st-card" style={{ cursor: "default" }} data-testid={`settings-style-${s.id}`}>
      <img src={s.image_url} alt={s.name} />
      <div style={{ padding: 10, display: "grid", gap: 8 }}>
        <input className="st-input" value={name} onChange={(e) => setName(e.target.value)} onBlur={saveName} maxLength={60} data-testid={`settings-style-${s.id}-name`} />
        <button className="st-btn st-btn--ghost st-btn--sm" onClick={() => ref.current.click()} disabled={busy} data-testid={`settings-style-${s.id}-replace`}><Upload size={13} /> {busy ? "Uploading…" : "Replace image"}</button>
        <input ref={ref} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => upload(e.target.files[0])} data-testid={`settings-style-${s.id}-file`} />
      </div>
    </div>
  );
};

const PaletteItem = ({ p, onLib }) => {
  const [name, setName] = useState(p.name);
  const [sw, setSw] = useState(p.swatches);
  const valid = sw.length >= 4 && sw.length <= 5 && sw.every((c) => HEX.test(c));
  const save = async () => {
    try { const { data } = await adminApi.put(`/studio/library/${p.id}`, { name: name.trim(), swatches: sw }); onLib(data); toast.success("Palette saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="st-pal" style={{ cursor: "default" }} data-testid={`settings-palette-${p.id}`}>
      <div className="st-swatches">{sw.map((c, i) => <i key={i} style={{ background: HEX.test(c) ? c : "#fff" }} />)}</div>
      <input className="st-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} data-testid={`settings-palette-${p.id}-name`} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {sw.map((c, i) => (
          <input key={i} className={`st-input ${HEX.test(c) ? "" : "is-error"}`} style={{ width: 92, height: 34, fontSize: 12 }} value={c} maxLength={7}
            onChange={(e) => setSw(sw.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`settings-palette-${p.id}-swatch-${i}`} />
        ))}
        {sw.length < 5 && <button className="st-btn st-btn--quiet st-btn--sm" onClick={() => setSw([...sw, "#FFFFFF"])} data-testid={`settings-palette-${p.id}-add`}>+ Swatch</button>}
        {sw.length > 4 && <button className="st-btn st-btn--quiet st-btn--sm" onClick={() => setSw(sw.slice(0, -1))} data-testid={`settings-palette-${p.id}-remove`}>− Swatch</button>}
      </div>
      <button className="st-btn st-btn--sm" disabled={!valid} onClick={save} data-testid={`settings-palette-${p.id}-save`}>Save palette</button>
    </div>
  );
};

export const StyleLibrary = () => {
  const [lib, setLib] = useState(null);
  useEffect(() => { adminApi.get("/studio/library").then((r) => setLib(r.data)); }, []);
  if (!lib) return null;
  return (
    <>
      <section className="st-panel" data-testid="settings-style-images">
        <h2 className="st-h2">Style images</h2>
        <p className="st-muted st-small" style={{ marginTop: -8 }}>Shown in Stage 1. Past selections keep the name and image they were chosen with.</p>
        <div className="st-cards">{lib.styles.map((s) => <StyleItem key={`${s.id}-${s.image_url}`} s={s} onLib={setLib} />)}</div>
      </section>
      <section className="st-panel" data-testid="settings-color-palettes">
        <h2 className="st-h2">Color palettes</h2>
        <p className="st-muted st-small" style={{ marginTop: -8 }}>4–5 hex swatches each.</p>
        <div className="st-cards">{lib.palettes.map((p) => <PaletteItem key={p.id} p={p} onLib={setLib} />)}</div>
      </section>
    </>
  );
};
